import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LoadingOverlay from 'react-loading-overlay';
import ScoreListItem from './components/ScoreListItem';
import ProgressBar from './components/ProgressBar';
import './MetabolismLanding.scss';
import assessmentImage from './images';
import { commonConstant } from '../../common';
import { fetchHealthSpanProfileAction } from '../../actions/healthSpanActions';
import { useScoreCategoryMetadata } from './useScoreCategoryMetadata';
import {
  HEALTH_SPAN_MAX_SCORE,
  SUB_CATEGORY_KEY_MAP,
  buildLandingScores,
  hasValidScore,
  getSubCategoryDisplayProps,
} from './healthSpanScore.utils';

const ROUTE_BY_KEY = {
  bmi: commonConstant.pathWeight,
  waist: commonConstant.pathWaist,
  bodyFat: commonConstant.pathFat,
};

const CATEGORIES = [
  {
    key: 'bmi',
    image: assessmentImage.bmiImage,
    name: 'BMI',
  },
  {
    key: 'waist',
    image: assessmentImage.waistImage,
    name: 'รอบเอว',
  },
  {
    key: 'bodyFat',
    image: assessmentImage.bodyFatImage,
    name: 'ไขมันในร่างกาย(%)',
  },
];

const MetabolismLanding = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { profile, loading } = useSelector((state) => state.healthSpan);
  const { metadata, metaLoading } = useScoreCategoryMetadata('metabolism');

  useEffect(() => {
    const wrapper = document.getElementById('wrapper');
    if (wrapper) wrapper.style.maxWidth = '100%';
    return () => {
      if (wrapper) wrapper.style.maxWidth = '';
    };
  }, []);

  useEffect(() => {
    dispatch(fetchHealthSpanProfileAction());
  }, [dispatch]);

  const { categoryScore, categoryStatus, scores } = buildLandingScores(
    profile,
    'metabolism',
    CATEGORIES,
    metadata
  );

  const hasValue = hasValidScore(categoryScore);
  const isLoading = loading || metaLoading;
  const tip = categoryStatus?.tip;
  const noDataColor = '#A1A1A1';
  const displayColor = hasValue ? categoryStatus.barColor : noDataColor;

  const handleCategoryClick = (category) => {
    const directRoute = ROUTE_BY_KEY[category.key];
    if (directRoute) {
      history.push(directRoute);
    }
  };

  const headerScore = hasValue ? categoryScore : '-';
  const headerMaxScore = categoryStatus?.maxScore || HEALTH_SPAN_MAX_SCORE;

  return (
    <div className="metabolism-landing-component">
      {isLoading && (
        <LoadingOverlay
          text="Loading"
          spinner
          active={true}
          styles={{ wrapper: { width: '100%', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 10 } }}
        >
          <div />
        </LoadingOverlay>
      )}

      {!isLoading && (
      <>
      <div className="bg-detail-container">
        <img src={assessmentImage.landingBgDetail} alt="" className="bg-detail" />
      </div>

      <div className="content-container">
        <div className="header-section">
          <div className="image-component">
            <img src={assessmentImage.metabolismImage} alt="" />
          </div>
          <div className="detail" style={{ color: displayColor }}>
            <div className="header-name">{metadata?.display_name || 'ระบบเผาผลาญ'}</div>
            <div className="status">{hasValue ? categoryStatus.statusText : 'ไม่มีข้อมูล'}</div>
            <div className="score-container">
              <div className="value">{headerScore}</div>
              <div className="max-value">/{headerMaxScore}</div>
            </div>
            <ProgressBar
              value={hasValue ? categoryScore : 0}
              maxValue={headerMaxScore}
              width="130px"
              barColor={displayColor}
            />
          </div>
        </div>

        {hasValue && tip && (
          <div className="tip-card">
            <div className="tip-label-container">
              <img src={assessmentImage.tipIcon} alt="" className="tip-label-image" />
              <div className="tip-label-text">คำแนะนำ</div>
            </div>
            <div className="tip-detail-container">
              <img src={tip.image || assessmentImage.tipMetabolismImage} alt="" className="image" />
              <div className="text-container">
                <div className="tip-text">{tip.title}</div>
                <div className="tip-detail-text">{tip.description}</div>
              </div>
            </div>
          </div>
        )}

        <div className="summary-score-list">
          <div className="title">ข้อมูลระบบเผาผลาญ</div>

          {CATEGORIES.map((category) => {
            const scoreData = scores[category.key] || {};
            const hasData = hasValidScore(scoreData.score);
            const apiKey = SUB_CATEGORY_KEY_MAP[category.key];
            const subName =
              metadata?.sub_categories?.[apiKey]?.display_name || category.name;
            const display = getSubCategoryDisplayProps(scoreData, hasData);

            return (
              <ScoreListItem
                key={category.key}
                image={category.image}
                name={subName}
                statusText={display.statusText}
                statusColor={display.barColor}
                value={hasData ? scoreData.score : '-'}
                maxValue={scoreData.maxScore || 0}
                barColor={display.barColor}
                valueColor={display.valueColor}
                hasData={hasData}
                onClick={() => handleCategoryClick(category)}
              />
            );
          })}
        </div>

        <div className="description-card">
          <div className="title">ระบบเผาผลาญ (Metabolic Health)</div>
          <div className="description-text">
            <span className="highlight">ประเมินความสมดุลของระบบทางเดินอาหาร</span>
            และจุลินทรีย์ในลำไส้ ซึ่งส่งผลต่อการย่อยอาหาร การขับถ่าย ภูมิคุ้มกัน และสุขภาพโดยรวม
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default MetabolismLanding;
